import { type Dayjs } from 'dayjs'
import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, moneySchema, toMoney } from '@/shared/api/schemas'
import { tenantToday } from '@/shared/lib/dayRange'
import type { Money } from '@/shared/lib/money'

export const ISO_DAY = 'YYYY-MM-DD'

export type DashboardPeriod = 'all' | 'today' | 'month' | 'custom'

/** `null` — butun davr */
export function periodRange(period: DashboardPeriod, custom: [Dayjs, Dayjs]): [Dayjs, Dayjs] | null {
  if (period === 'all') return null
  const today = tenantToday()
  if (period === 'today') return [today, today]
  if (period === 'month') return [today.startOf('month'), today]
  return custom
}

export const DASHBOARD_METRICS = [
  'totalSales',
  'totalCollected',
  'totalDiscounts',
  'outstanding',
  'cashCollected',
] as const
export type DashboardMetric = (typeof DASHBOARD_METRICS)[number]

export const DASHBOARD_FILTERS = ['branchId', 'centerId', 'vispId', 'cashierId', 'subscriberId', 'planId'] as const
export type DashboardFilterKey = (typeof DASHBOARD_FILTERS)[number]

type SingleFilterKey = Exclude<DashboardFilterKey, 'branchId'>

/** `from`/`to` bo'sh — butun davr; `branchIds` bo'sh — doiradagi hamma filial */
export type DashboardFilter = { from: string | null; to: string | null; branchIds: readonly string[] } & Partial<
  Record<SingleFilterKey, string | null>
>

const dashboardSchema = z.object({
  from: z.string().nullish(),
  to: z.string(),
  asOf: instantSchema,
  currency: z.string(),
  restricted: z.boolean(),
  totalSales: moneySchema,
  totalCollected: moneySchema,
  totalDiscounts: moneySchema,
  outstanding: moneySchema,
  cashCollected: moneySchema,
  notApplied: z.record(z.string(), z.array(z.string())).nullish(),
})

export interface FinanceDashboard {
  from: string | null
  to: string
  asOf: string
  currency: string
  restricted: boolean
  metrics: Record<DashboardMetric, Money>
  notApplied: Record<DashboardMetric, readonly string[]>
}

export function parseDashboard(data: unknown): FinanceDashboard {
  const raw = dashboardSchema.parse(data)
  const notApplied = raw.notApplied ?? {}
  const metrics = {} as Record<DashboardMetric, Money>
  const skipped = {} as Record<DashboardMetric, readonly string[]>
  for (const metric of DASHBOARD_METRICS) {
    metrics[metric] = toMoney(raw[metric])
    skipped[metric] = notApplied[metric] ?? []
  }
  return {
    from: raw.from ?? null,
    to: raw.to,
    asOf: raw.asOf,
    currency: raw.currency,
    restricted: raw.restricted,
    metrics,
    notApplied: skipped,
  }
}

export function ignoredFilters(
  dashboard: FinanceDashboard,
  metric: DashboardMetric,
  filter: DashboardFilter,
): DashboardFilterKey[] {
  return DASHBOARD_FILTERS.filter(
    (key) =>
      (key === 'branchId' ? filter.branchIds.length > 0 : Boolean(filter[key])) &&
      dashboard.notApplied[metric].includes(key),
  )
}

export async function fetchFinanceDashboard(filter: DashboardFilter, signal?: AbortSignal): Promise<FinanceDashboard> {
  const params = new URLSearchParams()
  if (filter.from && filter.to) {
    params.set('from', filter.from)
    params.set('to', filter.to)
  } else {
    params.set('allTime', 'true')
  }
  filter.branchIds.forEach((id) => params.append('branchId', id))
  for (const key of DASHBOARD_FILTERS) {
    const value = key === 'branchId' ? null : filter[key]
    if (value) params.set(key, value)
  }
  const response = await api.get<unknown>('/api/reports/finance-dashboard', {
    ...(signal ? { signal } : {}),
    params,
  })
  return parseDashboard(response.data)
}
